import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  inject,
} from '@angular/core';
import { TranslatePipe } from '@kirigami/shared/i18n';

/**
 * Data-safety band: the lock and the one-sentence assurance on a deep brand
 * blue, with the folded-paper artwork showing faintly through.
 *
 * It is static. Its height comes from its padding and its content, and nothing
 * the visitor does changes it. Until 2026-11-03 it was a drawer that opened as
 * it neared the top of the viewport, typed its lines out and wound back when
 * the visitor scrolled above it; every opening pushed the rest of the page down
 * under the reader, and the owner asked for a calm band instead.
 *
 * The only motion is a short fade and rise of the content the first time the
 * band comes into view. It moves nothing but the content's own paint
 * (opacity and transform), plays once, and is skipped entirely when the band
 * is already on screen at load, under reduced motion, and without
 * IntersectionObserver, in which cases the band simply renders as it is.
 */

/**
 * The content rises in once the band's top clears the bottom fifth of the
 * viewport. A margin rather than a ratio, so a band taller than the window
 * (zoomed text on a phone) still reaches it.
 */
const REVEAL_MARGIN = '0px 0px -20% 0px';

@Component({
  selector: 'app-encrypted-widget',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe],
  template: `
    <section id="encrypted" class="vault" aria-labelledby="encrypted-title">
      <div class="vault__fold" aria-hidden="true"></div>

      <div class="vault__content">
        <span class="vault__seal" aria-hidden="true">
          <img class="vault__lock" src="img/lock.svg" alt="" />
        </span>

        <h2 id="encrypted-title" class="vault__text">
          {{ 'landing.encrypted.lineOnePrefix' | t
          }}<span class="vault__em">{{ 'landing.encrypted.highlightOne' | t }}</span
          >,&nbsp;<span class="vault__em">{{ 'landing.encrypted.highlightTwo' | t }}</span
          >&ngsp;<span class="vault__em">{{ 'landing.encrypted.highlightThree' | t }}</span
          >.
        </h2>
      </div>
    </section>
  `,
  styles: [
    `
      /* Deep blue, lit softly from above the lock. The darkest point is the
         bottom corner of the linear stop, the lightest the centre of the glow;
         the text is checked against both. */
      .vault {
        position: relative;
        z-index: 0;
        overflow: clip;
        padding: 5rem 1.5rem;
        background:
          radial-gradient(60% 75% at 50% 0%, rgb(105 160 199 / 0.2), transparent 70%),
          linear-gradient(
            180deg,
            var(--kirigami-700) 0%,
            color-mix(in srgb, var(--kirigami-700) 80%, #000) 100%
          );
      }
      @media (min-width: 1024px) {
        .vault {
          padding: 7rem 4.0625rem;
        }
      }
      /* The folded-paper artwork, taken down to its light and shade so the
         creases read as a faint relief on the blue rather than as grey. A
         background rather than an <img>, so a missing file leaves the plain
         blue instead of a broken-image mark. */
      .vault__fold {
        position: absolute;
        inset: 0;
        z-index: -1;
        background: url('/img/encrypted-fold.png') center bottom / cover no-repeat;
        mix-blend-mode: soft-light;
        opacity: 0.55;
        pointer-events: none;
      }
      .vault__content {
        max-width: 56rem;
        margin: 0 auto;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 2rem;
        text-align: center;
      }
      .vault__seal {
        width: 4.75rem;
        height: 4.75rem;
        display: grid;
        place-items: center;
        border-radius: 999px;
        border: 1px solid rgb(203 221 236 / 0.24);
        background: rgb(255 255 255 / 0.06);
        box-shadow:
          0 0 0 0.5rem rgb(255 255 255 / 0.025),
          0 18px 40px rgb(10 25 40 / 0.35);
      }
      .vault__lock {
        width: 2.75rem;
        height: 2.75rem;
        display: block;
      }
      /* Large text throughout (24px and up), so 3:1 is the floor; the white
         and the kirigami-200 both clear 4.5:1 anyway. */
      .vault__text {
        margin: 0;
        font-size: clamp(1.5rem, 2.4vw, 2rem);
        font-weight: 600;
        line-height: 1.4;
        letter-spacing: -0.011em;
        text-wrap: balance;
        color: var(--neu-0);
      }
      /* The emphasis is a colour, not a box: a span rather than <mark>,
         because some screen readers announce <mark> as "highlighted", which is
         noise inside a heading. */
      .vault__em {
        color: var(--kirigami-200);
      }

      /* Armed only by the component, so a band that never gets an observer is
         simply visible. Opacity and transform only: the band's box is the same
         before, during and after. */
      .vault--armed .vault__content {
        opacity: 0;
        transform: translateY(1.25rem);
      }
      .vault--armed.vault--in .vault__content {
        opacity: 1;
        transform: none;
        transition:
          opacity 0.9s ease-out,
          transform 0.9s cubic-bezier(0.16, 1, 0.3, 1);
      }
      @media (prefers-reduced-motion: reduce) {
        .vault--armed .vault__content {
          opacity: 1;
          transform: none;
          transition: none;
        }
      }
    `,
  ],
})
export class EncryptedWidgetComponent implements AfterViewInit, OnDestroy {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  private observer?: IntersectionObserver;

  ngAfterViewInit(): void {
    if (
      typeof window === 'undefined' ||
      typeof matchMedia === 'undefined' ||
      typeof IntersectionObserver === 'undefined' ||
      matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }

    const section = this.host.nativeElement.querySelector<HTMLElement>('.vault');
    if (!section) return;

    // A band already on screen (a reload, or a back navigation that restores
    // the scroll level) is left alone: hiding it now would be a flicker.
    if (section.getBoundingClientRect().top < window.innerHeight) return;

    section.classList.add('vault--armed');
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        section.classList.add('vault--in');
        this.stopWatching();
      },
      { rootMargin: REVEAL_MARGIN },
    );
    this.observer.observe(section);
  }

  ngOnDestroy(): void {
    this.stopWatching();
  }

  private stopWatching(): void {
    this.observer?.disconnect();
    this.observer = undefined;
  }
}
